import React, { useMemo, useState } from 'react';
import {
  useTable,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table';
import PaginatedTable from './PaginatedTable';
import { appTableFeatures, type AppTableFeatures } from './tableFeatures';
import { format } from 'date-fns';
import type { AdminReport } from '../models/AdminReport';
import { Link } from 'react-router';

export default function AdminReportTable({
  data,
  impersonate,
  showPagination = true,
}: {
  data: AdminReport[];
  impersonate?: (email: string) => void;
  showPagination?: boolean;
}) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [globalFilter, setGlobalFilter] = React.useState('');

  const tableData = useMemo(() => data, [data]);

  const columnData: ColumnDef<AppTableFeatures, AdminReport>[] = useMemo(
    () => [
      {
        header: 'Updated',
        accessorKey: 'lastUpdated',
        sortFn: 'datetime',
        filterFn: 'equals',
        size: 120,
        cell: ({ row }) =>
          format(new Date(row.original.lastUpdated), 'MM/dd/yyyy'),
      },
      {
        header: 'Email',
        accessorKey: 'email',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 250,
        cell: ({ row }) =>
          impersonate ? (
            <button
              className='font-bold hover:opacity-70 dark:text-gray-300'
              onClick={() => impersonate(row.original.email)}
            >
              {row.original.email}
            </button>
          ) : (
            <Link
              className='font-bold hover:opacity-70 dark:text-gray-300'
              to={`/admin/${row.original.userId}`}
            >
              {row.original.email}
            </Link>
          ),
      },
      // {
      //   header: 'User ID',
      //   accessorKey: 'userId',
      //   sortFn: 'alphanumeric',
      //   filterFn: 'includesString',
      // },
      {
        header: 'Acc Created',
        accessorKey: 'accountCreated',
        sortFn: 'datetime',
        filterFn: 'equals',
        size: 140,
        cell: ({ row }) =>
          format(new Date(row.original.accountCreated), 'MM/dd/yyyy'),
      },
      {
        header: 'Acc Updated',
        accessorKey: 'accountUpdated',
        sortFn: 'datetime',
        filterFn: 'equals',
        size: 140,
        cell: ({ row }) =>
          format(new Date(row.original.accountUpdated), 'MM/dd/yyyy'),
      },
      {
        header: 'Alpacas',
        accessorKey: 'alpacaCount',
        sortFn: 'alphanumeric',
        filterFn: 'equals',
        size: 100,
      },
      {
        header: 'Images',
        accessorKey: 'imageCount',
        sortFn: 'alphanumeric',
        filterFn: 'equals',
        size: 100,
      },
      {
        header: 'Farms',
        accessorKey: 'farmCount',
        sortFn: 'alphanumeric',
        filterFn: 'equals',
        size: 100,
      },
      {
        header: 'Contacts',
        accessorKey: 'contactCount',
        sortFn: 'alphanumeric',
        filterFn: 'equals',
        size: 100,
      },
      {
        header: 'Events',
        accessorKey: 'eventCount',
        sortFn: 'alphanumeric',
        filterFn: 'equals',
        size: 100,
      },
      {
        header: 'Alpaca Events',
        accessorKey: 'alpacaEventCount',
        sortFn: 'alphanumeric',
        filterFn: 'equals',
      },
    ],
    [impersonate],
  );

  const columns = useMemo(() => columnData, [columnData]);

  const table = useTable({
    features: appTableFeatures,
    data: tableData,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: showPagination ? 10 : data.length,
      },
    },
    columnResizeMode: 'onChange',
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    paginateExpandedRows: false,
  });

  return (
    <PaginatedTable
      table={table}
      columnResizeMode='onChange'
      showFilters={true}
      showPagination={showPagination}
    />
  );
}
